/**
 * NavCommandPalette — Cmd/Ctrl+K power-user navigation.
 *
 * Groups results as: Favorites → Recent → All Modules (by sidebar
 * section). Falls back to fuzzy search via the built-in cmdk filter.
 */
import * as LucideIcons from "lucide-react";
import { Star, Clock, Sparkles } from "lucide-react";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty,
  CommandGroup, CommandItem, CommandSeparator, CommandShortcut,
} from "@/components/ui/command";
import { useNavShortcuts } from "./NavShortcuts";

const IconOf = ({ name, className }) => {
  const C = LucideIcons[name] || LucideIcons.Circle;
  return <C className={className} />;
};

export default function NavCommandPalette() {
  const {
    paletteOpen, closePalette, gotoRoute,
    pinned, recent, allRoutes, pinnedSet,
  } = useNavShortcuts();

  // Group "all modules" by sidebar section for a legible palette.
  const bySection = {};
  allRoutes.forEach((r) => {
    if (pinnedSet.has(r.route)) return;         // don't repeat pinned
    if (recent.find((x) => x.route === r.route)) return;  // don't repeat recent
    const k = r.section || "Other";
    (bySection[k] ||= []).push(r);
  });

  return (
    <CommandDialog open={paletteOpen} onOpenChange={(v) => !v && closePalette()}>
      <CommandInput
        placeholder="Jump to any module — try 'rm short', 'pallet', 'packing'…"
        data-testid="nav-palette-input"
      />
      <CommandList className="max-h-[70vh]">
        <CommandEmpty>No modules match.</CommandEmpty>

        {pinned.length > 0 && (
          <>
            <CommandGroup heading={
              <span className="flex items-center gap-1.5 text-rose-600">
                <Star className="w-3.5 h-3.5 fill-current" /> Favorites
              </span>
            }>
              {pinned.map((p) => (
                <CommandItem
                  key={p.route}
                  value={`⭐ ${p.label} ${p.route} ${p.section}`}
                  onSelect={() => gotoRoute(p.route)}
                  data-testid={`nav-palette-pinned-${p.route}`}
                >
                  <IconOf name={p.icon} className="mr-2 h-4 w-4 text-rose-600" />
                  <span className="flex-1">{p.label}</span>
                  <span className="text-[10px] text-zinc-400">{p.section}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {recent.length > 0 && (
          <>
            <CommandGroup heading={
              <span className="flex items-center gap-1.5 text-indigo-600">
                <Clock className="w-3.5 h-3.5" /> Recent
              </span>
            }>
              {recent.map((r) => (
                <CommandItem
                  key={r.route}
                  value={`🕒 ${r.label} ${r.route} ${r.section}`}
                  onSelect={() => gotoRoute(r.route)}
                  data-testid={`nav-palette-recent-${r.route}`}
                >
                  <IconOf name={r.icon} className="mr-2 h-4 w-4 text-indigo-600" />
                  <span className="flex-1">{r.label}</span>
                  <span className="text-[10px] text-zinc-400">{r.section}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {Object.entries(bySection)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([section, rows]) => (
            <CommandGroup key={section} heading={
              <span className="flex items-center gap-1.5 text-zinc-600">
                <Sparkles className="w-3.5 h-3.5" /> {section}
              </span>
            }>
              {rows.map((r) => (
                <CommandItem
                  key={r.route}
                  value={`${r.label} ${r.route} ${r.section}`}
                  onSelect={() => gotoRoute(r.route)}
                  data-testid={`nav-palette-item-${r.route}`}
                >
                  <IconOf name={r.icon} className="mr-2 h-4 w-4 text-zinc-500" />
                  <span className="flex-1">{r.label}</span>
                  <span className="text-[10px] text-zinc-400">{section}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          ))}

        <CommandSeparator />
        <div className="px-3 py-2 text-[10px] text-zinc-400 flex justify-between">
          <span>Press ↑↓ to navigate · Enter to jump</span>
          <span><CommandShortcut>Ctrl</CommandShortcut> +
                <CommandShortcut className="ml-1">K</CommandShortcut>
          </span>
        </div>
      </CommandList>
    </CommandDialog>
  );
}
